import { z } from "zod";

/**
 * Form schemas for the blog CMS.
 *
 * Every one of these uses a transform (`emptyToUndefined`, `trim`, coercion),
 * so input and output types differ and the forms take the three-generic
 * `useForm<Input, unknown, Output>` — the same shape `CampaignManager` needs.
 */

const emptyToUndefined = z.literal("").transform(() => undefined);
const coercedNumber = z.union([emptyToUndefined, z.coerce.number()]);

/** Kebab-case only, so an address can never carry a separator or a dot. */
export const BLOG_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]*$/;

export const blogGlossaryEntrySchema = z.object({
  term: z.string(),
  definition: z.string().min(1, "Every glossary term needs a definition in this locale"),
});

/**
 * Keywords, one per line (AM-18).
 *
 * Line-separated rather than comma-separated because a keyword is a phrase —
 * `"importer un CV"` — and a comma split would silently merge any phrase that
 * carries one. It also survives a script that punctuates differently (Arabic
 * writes its comma `،`), which is why the rule is a newline rather than a
 * cleverer comma: a newline is a newline everywhere.
 */
export const blogKeywordsSchema = z.string().transform((value) =>
  value
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line !== ""),
);

/**
 * One language copy.
 *
 * `fields` is a flat map of block-field values keyed by a synthetic name
 * (`f0`, `f1`, …) rather than by the API path: react-hook-form parses `[` and
 * `.` in a field name as structure, and `body[7].text` would register a nested
 * array instead of one string. The synthetic key maps back to the real path
 * through the descriptor list, which is what validation errors are matched on.
 */
export const blogLocaleFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  excerpt: z.string().min(1, "Excerpt is required"),
  pathSlug: z
    .union([
      emptyToUndefined,
      z.string().regex(BLOG_SLUG_PATTERN, "Lowercase letters, digits and hyphens only"),
    ])
    .optional(),
  keywords: blogKeywordsSchema,
  glossary: z.array(blogGlossaryEntrySchema),
  /**
   * This locale's fact renderings, keyed by the same synthetic control name the
   * block fields use and for the same reason: a fact id is free-form, and
   * `factValues.trial.days` would register two nested objects rather than one
   * string. `flattenFactValues` maps a key back to its id.
   *
   * A blank value is **accepted**. A translator who has not yet written
   * `14 Tage` is mid-job, not in error, and refusing the save would mean the
   * rest of their afternoon's German could not be stored either. The gap is
   * the server's `fact_value_missing`, which blocks the publish rather than the
   * draft.
   */
  factValues: z.record(z.string(), z.string()),
  fields: z.record(z.string(), z.string()),
});

export type BlogLocaleFormInput = z.input<typeof blogLocaleFormSchema>;
export type BlogLocaleFormOutput = z.output<typeof blogLocaleFormSchema>;

/**
 * The cluster-level values, which are not part of the locale form at all.
 *
 * Q14/Q15: fact *definitions* and `step.minutes` apply to every locale at once,
 * so they live outside the per-locale form with their own submit and their own
 * confirm. A shared dirty state would let a locale save carry a cluster change
 * nobody confirmed.
 *
 * AM-18: `value` is gone from this shape. A fact's rendering is language and
 * belongs to the copy that renders it; what stays here is what a translator
 * must never be able to change — which id the token names, and the `file:line`
 * that established the number.
 */
export const blogClusterFormSchema = z.object({
  facts: z
    .array(
      z.object({
        id: z.string().min(1, "A fact needs an id — it is what the token in the prose names"),
        source: z.string().min(1, "Say where this was verified"),
      }),
    )
    /**
     * Two facts sharing an id is newly expressible, because the id is newly
     * editable. It has to be caught here: downstream, `factValues` is keyed by
     * id, so the second definition would silently take the first one's
     * rendering in every language.
     */
    .superRefine((facts, ctx) => {
      facts.forEach((fact, index) => {
        if (facts.findIndex((candidate) => candidate.id === fact.id) !== index) {
          ctx.addIssue({
            code: "custom",
            path: [index, "id"],
            message: "Two facts cannot share an id",
          });
        }
      });
    }),
  minutes: z.array(
    z.object({
      index: z.number().int(),
      title: z.string(),
      minutes: coercedNumber.pipe(
        z.number().int("Whole minutes only").min(0, "Minutes cannot be negative"),
      ),
    }),
  ),
});

export type BlogClusterFormInput = z.input<typeof blogClusterFormSchema>;
export type BlogClusterFormOutput = z.output<typeof blogClusterFormSchema>;

/**
 * The upload form.
 *
 * Q19: the reason is required and short. An image swapped without a stated
 * reason is indistinguishable six months later from one swapped by accident.
 */
export const blogUploadFormSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, "Say why this variant is being replaced")
    .max(160, "Keep it to one line"),
  acceptDimensions: z.boolean(),
});

export type BlogUploadFormInput = z.input<typeof blogUploadFormSchema>;
export type BlogUploadFormOutput = z.output<typeof blogUploadFormSchema>;

/** The synthetic RHF key for the nth block field. */
export function fieldKey(index: number): string {
  return `f${String(index)}`;
}
